<!--
  Rechtliches → Fiskal-Provider (#613/#631, reworked).

  One card per legal area. The card STATES what is in force (which provider, and what the provider says about
  the stored credentials) before it offers anything that changes it — a provider switch is irreversible, so the
  facts come first and the action carries its own typed confirmation.

  Two things about the old markup are deliberately gone. The switch used to be an invisible `<div>` overlay
  covering the whole card: unreachable by keyboard, unnameable to a screen reader, and — because it covered the
  card — the reason axe reported every colour inside a card as "background could not be determined", i.e. the
  reason the contrast of the credential line had to be pinned by class instead of measured. It is now a real
  `<button>` per non-selected provider. The credential editor used to be a second absolute overlay with two
  placeholder-only fields; it is an ordinary labelled form below the tiles.

  The German labels the end-user documentation quotes verbatim are „Keiner" and „Cloud-TSE" (the provider
  cards, from `LegalProviderHelper`), the credential-state wording from `LegalAccountStateHelper`, „Key",
  „Secret", „Beheben", and the test-only warning on the „Keiner" card.

  „Cloud-TSE" replaced the supplier's own brand name in #241: nothing on a screen a customer or their tax
  adviser can reach says WHO performs the fiscal work, only what the thing is. The card label is rendered
  from `LegalProviderHelper.GetName` in exactly one place per card, and the confirmation dialog's
  `providerName` reads the same helper, so there is no second copy of the wording to drift.
-->
<div class="space-y-4">
  @if (!isOwner()) {
    <app-legal-readonly-hint/>
  }

  @for (setting of settings; track setting.legalType) {
    <section [attr.aria-labelledby]="'legal-area-' + setting.legalType + '-title'"
             class="rounded-2xl border-[1px] border-gray-100 bg-gray-50/50 p-4">

      <!-- Kopfzeile: gesetzlicher Bereich + Zustand der Zugangsdaten -->
      <div class="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <h3 [id]="'legal-area-' + setting.legalType + '-title'"
            class="text-base font-bold tracking-tight text-gray-800">
          {{ LegalTypeHelper.GetName(setting.legalType) }}
        </h3>

        @if (setting.legalProvider != LegalProvider.None) {
          <!-- One chip carrying the WHOLE value, at a measured token per state (style guide, "Status badge /
               pill"): green-700 on green-50 = 5.01:1, amber-700 on amber-50 = 4.85:1, red-700 on red-50 =
               5.91:1, gray-600 on gray-100 = 6.87:1. The -600 shades of all four fail AA at this size. -->
          @switch (setting.legalAccountState) {
            @case (LegalAccountState.CredentialsCorrect) {
              <span class="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 align-top text-xs
                           font-semibold text-green-700" data-testid="credential-state">
                {{ LegalAccountStateHelper.GetName(setting.legalAccountState) }}
              </span>
            }
            @case (LegalAccountState.CredentialsAvailable) {
              <span class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 align-top text-xs
                           font-semibold text-gray-600" data-testid="credential-state">
                {{ LegalAccountStateHelper.GetName(setting.legalAccountState) }}
              </span>
            }
            @case (LegalAccountState.CredentialsNotAvailable) {
              <span class="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 align-top text-xs
                           font-semibold text-amber-700" data-testid="credential-state">
                {{ LegalAccountStateHelper.GetName(setting.legalAccountState) }}
              </span>
            }
            @case (LegalAccountState.CredentialsUnauthorized) {
              <span class="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 align-top text-xs
                           font-semibold text-red-700" data-testid="credential-state">
                {{ LegalAccountStateHelper.GetName(setting.legalAccountState) }}
              </span>
            }
            @case (undefined) {
              <!-- The list has landed, the per-area `IsConfigured` answer has not. Say so, rather than
                   leaving a blank line where a state belongs. -->
              <span class="inline-flex animate-pulse items-center rounded-full bg-gray-100 px-2 py-0.5
                           align-top text-xs font-semibold text-gray-600" data-testid="credential-state">
                Status wird geprüft …
              </span>
            }
            @default {
              <!-- `Unknown` — the credential check itself failed. Same wording as the helper's own. -->
              <span class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 align-top text-xs
                           font-semibold text-gray-600" data-testid="credential-state">
                {{ LegalAccountStateHelper.GetName(LegalAccountState.Unknown) }}
              </span>
            }
          }
        }
      </div>

      <!-- Anbieter: die gewählte Karte zuerst, die Alternative daneben. -->
      <ul class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2" role="list">
        @for (provider of LegalProviderHelper.GetProviders; track provider) {
          <li class="flex">
            @if (setting.legalProvider == provider) {
              <div aria-current="true"
                   class="flex w-full items-start gap-3 rounded-xl border-[1px] border-theme-primary-400
                          bg-white p-3 shadow-sm">
                <span aria-hidden="true"
                      class="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full
                             bg-theme-primary-600 text-white">
                  <svg class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                    <path clip-rule="evenodd" fill-rule="evenodd"
                          d="M16.704 5.29a.75.75 0 0 1 .006 1.06l-7.5 7.583a.75.75 0 0 1-1.07-.005l-3.85-3.9a.75.75 0 1 1 1.068-1.054l3.318 3.361 6.968-7.044a.75.75 0 0 1 1.06-.006Z"/>
                  </svg>
                </span>
                <span class="min-w-0 flex-1">
                  <span class="block text-sm font-semibold text-gray-900">
                    {{ LegalProviderHelper.GetName(provider) }}
                  </span>
                  <span class="sr-only">Aktuell ausgewählt</span>

                  @if (provider == LegalProvider.None) {
                    <span class="mt-1 block text-xs font-semibold text-red-700">
                      Ausschließlich zur Testnutzung, nicht für den Produktivbetrieb geeignet!
                    </span>
                  }

                  @if (isOwner() && provider != LegalProvider.None) {
                    <span class="mt-2 block">
                      @if (setting.legalAccountState == LegalAccountState.CredentialsNotAvailable
                        || setting.legalAccountState == LegalAccountState.CredentialsUnauthorized) {
                        <!-- „Beheben" — the documented affordance that opens the Key/Secret fields. Red
                             because the state it answers is a blocking one. -->
                        <button (click)="edit(setting)" data-testid="edit-credentials" type="button"
                                class="inline-flex items-center gap-1.5 rounded-lg bg-red-50 px-3 py-1.5
                                       text-sm font-medium text-red-700 transition-colors duration-200
                                       hover:bg-red-100 focus-visible:outline focus-visible:outline-2
                                       focus-visible:outline-offset-2 focus-visible:outline-red-500">
                          <svg aria-hidden="true" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path clip-rule="evenodd" fill-rule="evenodd"
                                  d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 5Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"/>
                          </svg>
                          Beheben
                        </button>
                      } @else {
                        <button (click)="edit(setting)" data-testid="edit-credentials" type="button"
                                class="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm
                                       font-medium text-gray-600 transition-colors duration-200
                                       hover:bg-gray-100 hover:text-gray-900 focus-visible:outline
                                       focus-visible:outline-2 focus-visible:outline-offset-2
                                       focus-visible:outline-theme-primary-500">
                          <svg aria-hidden="true" class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                            <path
                              d="M21 12a1 1 0 0 0-1 1v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h6a1 1 0 0 0 0-2H5a3 3 0 0 0-3 3v14a3 3 0 0 0 3 3h14a3 3 0 0 0 3-3v-6a1 1 0 0 0-1-1m-15 .76V17a1 1 0 0 0 1 1h4.24a1 1 0 0 0 .71-.29l6.92-6.93L21.71 8a1 1 0 0 0 0-1.42l-4.24-4.29a1 1 0 0 0-1.42 0l-2.82 2.83l-6.94 6.93a1 1 0 0 0-.29.71m10.76-8.35l2.83 2.83l-1.42 1.42l-2.83-2.83ZM8 13.17l5.93-5.93l2.83 2.83L10.83 16H8Z"/>
                          </svg>
                          Zugangsdaten
                        </button>
                      }
                    </span>
                  }
                </span>
              </div>
            } @else if (isOwner()) {
              <!-- The switch. A real button, named for what it does, so it is reachable by keyboard and
                   announceable; `data-testid` is the spec's handle, independent of the layout classes. -->
              <button (click)="selected(provider, setting)" data-testid="select-provider" type="button"
                      [attr.aria-label]="'Auf ' + LegalProviderHelper.GetName(provider) + ' wechseln'"
                      class="flex w-full items-start gap-3 rounded-xl border-[1px] border-gray-200 bg-white
                             p-3 text-left shadow-sm transition-all duration-200
                             hover:border-theme-primary-300 hover:bg-theme-primary-50/40 active:scale-95
                             focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
                             focus-visible:outline-theme-primary-500">
                <span aria-hidden="true"
                      class="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 rounded-full border-[1px]
                             border-gray-300 bg-white"></span>
                <span class="min-w-0 flex-1">
                  <span class="block text-sm font-semibold text-gray-900">
                    {{ LegalProviderHelper.GetName(provider) }}
                  </span>
                  <span class="mt-0.5 block text-xs text-gray-500">Wechseln — nicht umkehrbar</span>
                </span>
              </button>
            } @else {
              <div class="flex w-full items-start gap-3 rounded-xl border-[1px] border-gray-200 bg-white/60
                          p-3">
                <span aria-hidden="true"
                      class="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 rounded-full border-[1px]
                             border-gray-300 bg-white"></span>
                <span class="block min-w-0 flex-1 text-sm font-medium text-gray-600">
                  {{ LegalProviderHelper.GetName(provider) }}
                </span>
              </div>
            }
          </li>
        }
      </ul>

      @if (isOwner() && setting.isEditing) {
        <!-- Zugangsdaten-Editor. Below the tiles rather than over them, with visible labels: „Key" and
             „Secret" are the words the end-user documentation names, and a placeholder is not a label.
             Saving is guarded the same way as a provider switch (#896): a registered device or an open cash
             point refuses the save with the same two codes/sentences #629 gave the switch, whether or not the
             save also changes the provider — hence the hint below, ahead of the form rather than after a
             refusal. -->
        <form (submit)="submitCredentials($event, setting, keyField.value, secretField.value)"
              class="mt-3 rounded-xl border-[1px] border-gray-200 bg-white p-3 shadow-sm"
              data-testid="credential-editor">
          <p class="text-sm font-bold tracking-tight text-gray-700">Zugangsdaten</p>
          <p class="mt-1 text-xs text-gray-500">
            Key und Secret des Provider-Kontos für {{ LegalTypeHelper.GetName(setting.legalType) }}. Das
            Secret wird nach dem Speichern nicht mehr angezeigt.
          </p>
          <p class="mt-1 text-xs text-gray-500" data-testid="credential-editor-hint">
            Eine Änderung ist nur möglich, solange dieser Standort keine registrierten Geräte und keinen
            offenen Kassenabschluss hat.
          </p>

          <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label [for]="'legal-key-' + setting.legalType"
                     class="mb-2 block text-sm font-bold tracking-tight text-gray-700">Key</label>
              <input #keyField [id]="'legal-key-' + setting.legalType" autocomplete="off" required type="text"
                     class="block w-full rounded-xl border-[1px] border-gray-200 bg-white px-3 py-2.5
                            text-gray-900 shadow-sm transition-all duration-200 placeholder-gray-300
                            focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500
                            sm:text-sm">
            </div>
            <div>
              <label [for]="'legal-secret-' + setting.legalType"
                     class="mb-2 block text-sm font-bold tracking-tight text-gray-700">Secret</label>
              <input #secretField [id]="'legal-secret-' + setting.legalType" autocomplete="new-password"
                     required type="password"
                     class="block w-full rounded-xl border-[1px] border-gray-200 bg-white px-3 py-2.5
                            text-gray-900 shadow-sm transition-all duration-200 placeholder-gray-300
                            focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500
                            sm:text-sm">
            </div>
          </div>

          <div class="mt-3 flex flex-wrap justify-end gap-3">
            <button (click)="cancelEdit(setting)" type="button"
                    class="inline-flex items-center justify-center rounded-2xl border border-gray-200 px-4
                           py-2.5 text-sm font-semibold text-gray-600 shadow-sm transition-all duration-200
                           hover:border-gray-300 hover:bg-gray-50 active:scale-95">
              Abbrechen
            </button>
            <button data-testid="save-credentials" type="submit"
                    class="inline-flex items-center justify-center rounded-2xl bg-theme-primary-600 px-5
                           py-2.5 text-sm font-semibold text-white shadow-lg shadow-theme-primary-100
                           transition-all duration-200 hover:bg-theme-primary-700 hover:shadow-xl
                           active:scale-95">
              Speichern
            </button>
          </div>
        </form>
      }
    </section>
  }
</div>

@if (pendingSwitch(); as pending) {
  <app-provider-switch-confirm-dialog (cancelled)="cancelSwitch()" (confirmed)="confirmSwitch()"
                                      [areaName]="LegalTypeHelper.GetName(pending.setting.legalType)"
                                      [busy]="switching()"
                                      [providerName]="LegalProviderHelper.GetName(pending.provider)"/>
}
